/* OAuth-Client detail layout, ported 1:1 from the Claude Design prototype
 * (pim-admin/pages.jsx ClientPage). DS Card supplies the white card shell;
 * these styles reproduce the header card, card-heads, URI rows, secret box,
 * scope rows, the `.kv` config lists and the delete card the DS lacks. */

/* two-column layout — design `.grid-360` (1.55fr / 1fr) */
.grid {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: var(--spacing-4);
  align-items: start;
}
.stack {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}
@media (width <= 1000px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

/* ── header card ── */
.headerRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
  flex-wrap: wrap;
  padding: var(--spacing-5);
}
.headerLeft {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}
.markSquare {
  width: 52px;
  height: 52px;
  border-radius: 9px;
  background: var(--color-primary-lighter);
  color: var(--color-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.titleRow {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  flex-wrap: wrap;
}
.title {
  font-size: var(--font-size-xl);
  letter-spacing: -0.01em;
  color: var(--color-text);
}
.metaRow {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  margin-top: var(--spacing-1);
  flex-wrap: wrap;
}
.idPill {
  border: none;
  cursor: pointer;
  background: var(--color-grey-lightest);
  padding: 2px 7px;
  border-radius: 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: var(--font-size-s);
  color: var(--color-text);
}
.idPill:hover {
  background: var(--color-grey-lighter);
}
.headerActions {
  display: flex;
  gap: var(--spacing-2);
  flex-shrink: 0;
  align-items: center;
}

/* ── card head (bordered, with leading icon) ── */
.cardHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-5);
  border-bottom: 1px solid var(--color-grey-lightest);
  min-height: 60px;
}
.cardHeadTitle {
  font-size: var(--font-size-m);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: var(--color-text);
}
.cardHeadActions {
  display: flex;
  gap: var(--spacing-2);
  align-items: center;
}
.bodyPad {
  padding: var(--spacing-5);
}

/* card heading inside a padded card (design `.h3`) */
.h3 {
  font-size: var(--font-size-m);
  color: var(--color-text);
  margin: 0 0 var(--spacing-3);
}

/* ── URI rows (design `.uri-row`) ── */
.uriRow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  border-bottom: 1px solid var(--color-grey-lightest);
}
.uriRow:last-child {
  border-bottom: none;
}
.uriRowAdd {
  background: var(--color-primary-lightest);
}
.uriVal {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-s);
  color: var(--color-text);
  background: var(--color-grey-lightest);
  padding: 6px 10px;
  border-radius: 7px;
  overflow-x: auto;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.uriInput {
  flex: 1;
  min-width: 0;
}
.uriEmpty {
  padding: 14px 16px;
  font-size: var(--font-size-m);
  color: var(--color-grey);
}

/* ── scope rows (design `.prow` with `.diag__ic` bubble) ── */
.scopeRow {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 11px 0;
  border-bottom: 1px solid var(--color-grey-lightest);
}
.scopeRow:last-child {
  border-bottom: none;
}
.scopeIc {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.scopeIcCore {
  background: var(--color-primary-lightest);
  color: var(--color-primary);
}
.scopeIcSensitive {
  background: var(--color-warning-lightest);
  color: var(--color-warning);
}
.scopeMain {
  flex: 1;
  min-width: 0;
}
.scopeName {
  font-size: var(--font-size-m);
}
.scopeMeta {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: 2px;
}
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.codepill {
  background: var(--color-grey-lightest);
  padding: 2px 7px;
  border-radius: 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: var(--font-size-s);
}
.chipRow {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* ── secret box (design `.secret-box`) ── */
.secretBox {
  display: flex;
  align-items: center;
  gap: 8px;
}
.secretVal {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-m);
  letter-spacing: 0.04em;
  background: var(--color-grey-lightest);
  padding: 9px 12px;
  border-radius: 8px;
  overflow-x: auto;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.muted {
  color: var(--color-grey);
  font-size: var(--font-size-m);
}
.hint {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: var(--spacing-2);
  line-height: 1.45;
}

/* ── public-client / generic gated box (design `.gated`) ── */
.gated {
  border: 1px solid var(--color-grey-lighter);
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--color-grey-lightest);
}
.gatedDanger {
  border-color: var(--color-error-lighter);
}
.gatedIc {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--color-white);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-grey);
}
.gatedIcDanger {
  color: var(--color-error);
}
.gatedMain {
  flex: 1;
  min-width: 0;
}
.gatedTitle {
  font-size: var(--font-size-m);
  color: var(--color-text);
}
.gatedText {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: 2px;
}

/* ── linked application prow row ── */
.prow {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 14px 16px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  border: none;
  background: transparent;
  font-family: inherit;
  text-decoration: none;
  color: inherit;
}
.prow:hover {
  background: var(--color-primary-lightest);
}
.prowMark {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--color-primary-lighter);
  color: var(--color-primary-dark);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.prowMain {
  flex: 1;
  min-width: 0;
}
.prowName {
  font-size: var(--font-size-m);
  color: var(--color-text);
}
.prowMeta {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: 2px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.prowChev {
  color: var(--color-grey-light);
  flex-shrink: 0;
}
.prowEmpty {
  padding: 14px 16px;
  font-size: var(--font-size-m);
  color: var(--color-grey);
}
